/**
 * Copyright (c) 2022 XYLink Inc.
 *
 * XY Electron Slave SDK Lib
 *
 * @summary 小鱼易连副屏SDK，用来在多窗口多屏幕下显示远端视频流；
 *
 * Created at     : 2022-05-27 14:49:50
 * Last modified  : 2024-12-03 17:48:04
 */

import Logger from './tools/logger';
import {
  ISlaveConfig,
  IVideoFrame,
  SDKType,
  IEventMap,
  IMasterParams,
  IModel,
  LayoutModel,
  TemplateModel,
  XYReturnResult
} from './type/index';
import {
  VERSION,
  EVENT,
  DEFAULT_MASTER_CONFIG,
  SUCCESS_CODE,
} from './tools/enum';
import LayoutManager from './layout';
import { getDllPath, getLogPath, SDK_SLAVE_LOG_NAME } from './tools/helper';
import { getStatus, TYPEOF } from './tools/utils';
import { Base64 } from './tools/base64';

const os = require('os');
const platform = os.platform();
let SdkWrapper = null;

const EventEmitter = require('events').EventEmitter;
if (platform === 'win32') {
  SdkWrapper = require('../../build/Release/xy-electron-render-sdk.node');
}
const path = require('path');

class XYSlaveRTC extends EventEmitter {
  public sdk: any;
  public instance: XYSlaveRTC;
  // constructor config data
  public config: ISlaveConfig;
  // log class
  private logger: Logger;
  // Layout管理器
  private layoutManager: LayoutManager;

  constructor(config) {
    super();

    this.config = {
      offset: [92, 0],
      // 布局模式：auto：自动布局，custom：自定义布局
      model: LayoutModel.CUSTOM,
      // 入会是否自动静音
      muteAudio: false,
      // 入会是否自动关闭摄像头
      muteVideo: false,
      templateModel: 'GALLERY',
      ...config,
      dllPath: config.dllPath || '',
      logPath: config.logPath || '',
    };

    this.config.model = this.config.model.toUpperCase() as IModel;

    const { sdkLogPath, sdkLogDir, sdkSlaveLogPath } = getLogPath(
      config.logPath || '',
      SDK_SLAVE_LOG_NAME
    );
    this.logger = new Logger(sdkLogDir, sdkLogPath, SDK_SLAVE_LOG_NAME, () => {
      this.initSDKAll(sdkLogDir, sdkLogPath, sdkSlaveLogPath);
    });
  }

  private initSDKAll(sdkLogDir, sdkLogPath, sdkSlaveLogPath) {
    this.instance = null;

    this.sdk = SdkWrapper ? SdkWrapper.getInstance() : {};

    this.logger.log('Merged Config:', this.config);
    this.logger.log('LogPath: ', {
      sdkLogPath,
      sdkLogDir,
      sdkSlaveLogPath,
    });

    this.sdk.initialize({ sdkLogPath, sdkLogDir, sdkSlaveLogPath });

    this.layoutManager = new LayoutManager(this, SDKType.SLAVE);

    this.initNemoSDK();

    this.logger.log('[Slave]Electron SDK Version:', VERSION);
    this.logger.log('[Slave]Electron SDK Config:', this.config);
    this.logger.log('[Slave]this.sdk:', this.sdk);

    this.initEventHandler();
    this.layoutManager.initWindowResizeEvent();
  }

  /**
   * 获取 XYRTC 实例
   * 每次入会时，都需要调用此方法获取实例，仅针对当此会议有效！
   *
   * @static
   */
  static getXYInstance(config: ISlaveConfig) {
    if (!this.instance) {
      this.instance = new XYSlaveRTC(config || {});
    }

    return this.instance as XYSlaveRTC;
  }

  /**
   * 获取SDK初始化配置数据
   *
   * @returns { IConfig } sdk init config params
   */
  public getConfig() {
    return this.config;
  }

  /**
   * Get sdk version info
   *
   * @public
   * @returns { string } version
   */
  public getVersion() {
    return '_BUILDVERSION_';
  }
  /**
   * 获取日志路径
   *
   * @param { string } logPath
   * @returns logDirPath
   */
  public getLogPath(logPath: string) {
    const appData = process.env.APPDATA;
    let logDirPath = appData + '\\xylink-electron\\';

    if (logPath) {
      const isAbsolute = path.isAbsolute(logPath);

      if (isAbsolute) {
        logDirPath = path.join(logPath);
      } else {
        logDirPath = path.join(appData + '\\', logPath + '\\');
      }
    }

    return logDirPath;
  }

  /**
   * Get dlls path and Init Nemo SDK
   *
   * @private
   */
  private initNemoSDK() {
    const currentDirection = this.sdk.getCurrentDirectory();

    this.logger.log('currentDirection: ', currentDirection);

    const { sdkDllPath, sdkProjectPath } = getDllPath(
      this.config.dllPath,
      currentDirection,
      'NemoSlaveSDK.dll'
    );

    this.config.sdkDllPath = sdkDllPath;
    this.config.sdkProjectPath = sdkProjectPath;

    this.sdk.InitSDK({
      sdkDllPath,
      sdkProjectPath,
    });
  }

  /**
   * Get video frame buffer data by c++
   *
   * @public
   * @param { string } sourceId 用户资源id
   * @param { boolean } isContent 是否是共享Content内容
   * @returns { IFrame } Video frame buffer data
   */
  public getVideoFrame(sourceId, isContent) {
    return this.sdk.getVideoFrame(sourceId, isContent);
  }

  /**
   * 清理Slave SDK资源
   */
  public destroy() {
    this.logger.log('destroy slave sdk');

    this.layoutManager.destroy();
    this.config = {};
    this.logger = null;
    this.layoutManager = null;
  }

  /**
   * Set log level
   * level: "INFO" | "DEBUG" | "WARNING" | "ERROR" | "NONE"
   *
   * @public
   */
  public setLogLevel(level: 'INFO' | 'DEBUG' | 'WARNING' | 'ERROR' | 'NONE') {
    this.logger.setLogLevel(level);
  }

  /**
   * 停止所有的渲染器
   *
   * @public
   */
  public stopAllVideoRender() {
    this.logger.log('[Slave]stopAllVideoRender func invoke');

    this.layoutManager.stopAllVideoRender();
  }

  /**
   * 切换布局
   * @param templateModel 布局模式 CONTENT 5种 NORMAL 2种 共7种
   * @returns
   */
  public switchLayout(templateModel?: TemplateModel) {
    this.logger.log('[Slave] switchLayout func invoke.');

    return new Promise((resolve, reject) => {
      const res = this.layoutManager.switchLayout(templateModel);

      if (res?.code === SUCCESS_CODE) {
        resolve(res);
      } else {
        reject(res);
      }
    });
  }

  /**
   * 发送消息
   *
   * @param { keyof IEventMap } key 消息key
   * @param { any } data 消息内容
   */
  public emitMessage(key: keyof IEventMap, data: any) {
    this.logger.log('emit message: ', { key, data });

    this.emit(key, data);
  }

  /**
   * 绑定sourceId和canvas，代理渲染视频
   *
   * @public
   * @param sourceId sourceId，通过回调：EVENT.RTC_EVENT_VIDEO_STREAMS 返回给业务
   * @param canvasEle canvas id，由第三方提供canvas元素id，后续由sdk接管使用webgl渲染视频
   */
  public setVideoRender(
    sourceId: string,
    canvasEle: HTMLCanvasElement | string
  ) {
    this.logger.log('[Slave]setVideoRender func:', sourceId, typeof canvasEle);

    return this.layoutManager.setVideoRender(sourceId, canvasEle);
  }

  /**
   * 注册新创建渲染画面
   *
   * @returns { Promise<XYReturnResult> } common status data
   */
  public startSlave(params: IMasterParams) {
    return new Promise((resolve, reject) => {
      this.logger.log('startSlave func invoke.', params);

      if (!TYPEOF.isObject(params)) {
        this.logger.log('params need receive object value');
        return reject(
          getStatus('XYSDK:960101', {
            params,
          })
        );
      }

      const { deviceID = 'ex_screen_1' } = params || {};
      const masterConfig = Object.assign({}, DEFAULT_MASTER_CONFIG, {
        deviceID,
      });

      this.logger.log('start slave merge params: ', masterConfig);

      if (this.sdk) {
        this.sdk.startSlave(masterConfig);
      } else {
        this.logger.warn('not init sdk instance');
      }

      return resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * 停止新新窗口渲染
   *
   * @returns { Promise<XYReturnResult> } common status data
   */
  public stopSlave() {
    return new Promise((resolve) => {
      this.logger.log('stopSlave func invoke.');

      if (this.sdk) {
        this.destroy();
        this.sdk.stopSlave();
      } else {
        this.logger.warn('not init sdk instance');
      }

      resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * register c++ bridge event message
   *
   * @private
   */
  private initEventHandler() {
    this.logger.log('[Slave]init c++ event handler');

    /**
     * 回调VideoFrame数据，已废弃
     *
     * @deprecated
     */
    this.sdk.registerEvent(
      EVENT.RTC_EVENT_Video_Frame_Ready,
      (e: IVideoFrame) => {}
    );

    /**
     * 请流结果数据
     */
    this.sdk.registerEvent(
      EVENT.RTC_VIDEO_STREAM_CHANGED_IN_SLAVE,
      (streams: any) => {
        this.logger.log('Get sdk event video streams: ', streams);
        const nextStreams = [];

        streams.forEach((stream) => {
          const { displayName } = stream;

          nextStreams.push({
            ...stream,
            displayName: Base64.decode(displayName),
          });
        });

        this.logger.log('nextStreams: ', nextStreams);

        this.layoutManager.setStreams(nextStreams);
        this.layoutManager.updateRenderers();
        this.layoutManager.createVideoStreamLayout();
      }
    );
  }
}

export { XYSlaveRTC };
